import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { getWeightLogs, updateWeightLog, deleteWeightLog, getDashboard } from '../lib/api';
import { ChevronLeft, Pencil, Trash2, X } from 'lucide-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { OfflinePage } from '../components/OfflineComponents';

export default function StoricoPesi() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const { formatNumber, convertWeight, weightUnit, convertWeightToKg } = useUnits();
  const isOnline = useOnlineStatus();
  const [logs, setLogs] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editWeight, setEditWeight] = useState(80);
  const [editDecimal, setEditDecimal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Generate range based on unit (kg: 20-200, lbs: 44-440)
  const isLbs = weightUnit === 'lbs';
  const wholeRange = isLbs 
    ? Array.from({ length: 397 }, (_, i) => i + 44) // 44-440 lbs
    : Array.from({ length: 181 }, (_, i) => i + 20); // 20-200 kg
  const decimalRange = Array.from({ length: 10 }, (_, i) => i);

  const loadData = useCallback(async () => {
    try {
      setNetworkError(false);
      const [logsRes, dashRes] = await Promise.all([
        getWeightLogs(),
        getDashboard()
      ]);
      setLogs(logsRes.data.reverse());
      setPlan(dashRes.data?.plan || null);
    } catch (err) {
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // window.scrollTo(0, 0); -- removed: App.js handles scroll restoration
    loadData();
  }, [loadData]);


  const startEdit = (log) => {
    // Convert from kg to display unit
    const displayWeight = convertWeight(log.weight);
    const whole = Math.floor(displayWeight);
    const decimal = Math.round((displayWeight - whole) * 10);
    setEditWeight(whole);
    setEditDecimal(decimal);
    setEditingLog(log);
  };

  const saveEdit = async () => {
    if (!editingLog) return;
    setSaving(true);
    try {
      // Get weight in display unit and convert back to kg for storage
      const displayWeight = editWeight + (editDecimal / 10);
      const weightInKg = convertWeightToKg(displayWeight);
      await updateWeightLog(editingLog.log_id, { weight: weightInKg, mood: editingLog.mood });
      setEditingLog(null);
      loadData();
    } catch (err) {
      console.error('Error updating log:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    try {
      await deleteWeightLog(logId);
      setConfirmDelete(null);
      loadData();
    } catch (err) {
      console.error('Error deleting log:', err);
    }
  };

  const getExpected = (dateStr) => {
    if (!plan || !plan.initial_weight || !plan.target_weight) return null;
    
    const start = new Date(plan.start_date);
    const end = new Date(plan.end_date);
    const current = new Date(dateStr);
    
    const totalDays = (end - start) / (1000 * 60 * 60 * 24);
    if (totalDays <= 0) return null;
    
    const elapsedDays = (current - start) / (1000 * 60 * 60 * 24);
    const progress = Math.min(1, Math.max(0, elapsedDays / totalDays));
    
    const totalLoss = plan.initial_weight - plan.target_weight;
    const expectedLoss = totalLoss * (1 - Math.exp(-3 * progress));
    const result = plan.initial_weight - expectedLoss;
    
    return isNaN(result) ? null : result.toFixed(1);
  };

  // Get locale for date formatting based on language
  const getLocale = () => {
    const localeMap = {
      it: 'it-IT',
      en: 'en-US',
      es: 'es-ES',
      pt: 'pt-BR',
      de: 'de-DE',
      fr: 'fr-FR'
    };
    return localeMap[language] || 'en-US';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(getLocale(), { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const formatDateShort = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(getLocale());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Show offline page if no data and offline
  if ((!isOnline || networkError) && logs.length === 0) {
    return <OfflinePage onRetry={loadData} />;
  }

  return (
    <div className={`${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`} data-testid="storico-pesi-page">
      {/* Fixed Header - with safe area */}
      <div className={`fixed-header px-4 pb-4 flex items-center gap-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-200'} shadow-sm`}>
        <div className="max-w-md mx-auto w-full flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}
          >
            <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <h1 className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('weightHistory.title')}
          </h1>
        </div>
      </div>

      {/* Scrollable Content - with padding top for fixed header and bottom for nav */}
      <div className="content-below-header pb-32 px-4">
        {logs.length === 0 ? (
          <div className="text-center py-12">
            <p className={isDark ? 'text-white/50' : 'text-gray-500'}>
              {t('weightHistory.noRecords')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map(log => {
              const expected = getExpected(log.date);
              const weightConverted = convertWeight(log.weight);
              const expectedConverted = expected ? convertWeight(parseFloat(expected)) : null;
              const diff = expectedConverted ? (weightConverted - expectedConverted).toFixed(2) : null;
              const diffNum = diff ? parseFloat(diff) : 0;
              const status = !diff ? 'on' : Math.abs(diffNum) < 0.1 ? 'on' : diffNum < 0 ? 'under' : 'over';

              return (
                <div 
                  key={log.log_id}
                  className={`glass-card p-4 ${isDark ? '' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        {formatDate(log.date)}
                      </p>
                      <p className={`font-display text-3xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatNumber(weightConverted, 1)} <span className="text-lg font-normal">{weightUnit}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {diff && (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          status === 'under' ? 'bg-green-500/10 text-green-500' :
                          status === 'over' ? 'bg-red-500/10 text-red-500' :
                          'bg-blue-500/10 text-blue-500'
                        }`}>
                          {diffNum > 0 ? '+' : ''}{formatNumber(diffNum, 2)} {weightUnit}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    {expectedConverted && (
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                        {t('weightHistory.theoreticalLine')}: {formatNumber(expectedConverted, 1)} {weightUnit}
                      </p>
                    )}
                    <div className="flex gap-2 ml-auto">
                      <button 
                        onClick={() => startEdit(log)}
                        className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 ${
                          isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        {t('weightHistory.edit')}
                      </button>
                      <button 
                        onClick={() => setConfirmDelete(log)}
                        className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1 bg-red-500/10 text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingLog && (
        <EditWeightModal 
          log={editingLog}
          isDark={isDark}
          editWeight={editWeight}
          setEditWeight={setEditWeight}
          editDecimal={editDecimal}
          setEditDecimal={setEditDecimal}
          wholeRange={wholeRange}
          decimalRange={decimalRange}
          weightUnit={weightUnit}
          saving={saving}
          onSave={saveEdit}
          onClose={() => setEditingLog(null)}
          t={t}
          formatDate={formatDate}
        />
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <DeleteConfirmModal 
          log={confirmDelete}
          isDark={isDark}
          onDelete={() => handleDelete(confirmDelete.log_id)}
          onClose={() => setConfirmDelete(null)}
          t={t}
          formatDateShort={formatDateShort}
        />
      )}
    </div>
  );
}

function EditWeightModal({ log, isDark, editWeight, setEditWeight, editDecimal, setEditDecimal, wholeRange, decimalRange, weightUnit, saving, onSave, onClose, t, formatDate }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-hidden"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <h3 className={`font-display text-lg mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('weightHistory.editWeight')}
        </h3>
        <p className={`text-sm text-center mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {formatDate(log.date)}
        </p>

        <div className="text-center mb-3">
          <span className="font-display text-4xl font-bold text-[#1d3557]">
            {editWeight}.{editDecimal}
          </span>
          <span className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-500'}`}> {weightUnit}</span>
        </div>

        <div className={`flex items-center justify-center rounded-xl overflow-hidden h-40 mb-6 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          <div className="relative h-full w-24 overflow-hidden">
            <div 
              className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
              onScroll={(e) => {
                const idx = Math.round(e.target.scrollTop / 40);
                if (wholeRange[idx] !== undefined) setEditWeight(wholeRange[idx]);
              }}
              ref={(el) => {
                if (el && !el.dataset.init) {
                  el.dataset.init = 'true';
                  const idx = wholeRange.indexOf(editWeight);
                  if (idx !== -1) el.scrollTop = idx * 40;
                }
              }}
            >
              <div className="h-[60px]"></div>
              {wholeRange.map(w => (
                <div 
                  key={w}
                  className={`h-10 flex items-center justify-center snap-center font-display ${
                    editWeight === w ? 'text-[#1d3557] font-bold text-2xl' : isDark ? 'text-white/30' : 'text-gray-300'
                  }`}
                >
                  {w}
                </div>
              ))}
              <div className="h-[60px]"></div>
            </div>
            <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-10 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
          </div>

          <span className={`font-display text-2xl ${isDark ? 'text-white/30' : 'text-gray-300'}`}>.</span>

          <div className="relative h-full w-16 overflow-hidden">
            <div 
              className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
              onScroll={(e) => {
                const idx = Math.round(e.target.scrollTop / 40);
                if (decimalRange[idx] !== undefined) setEditDecimal(decimalRange[idx]);
              }}
              ref={(el) => {
                if (el && !el.dataset.init) {
                  el.dataset.init = 'true';
                  el.scrollTop = editDecimal * 40;
                }
              }}
            >
              <div className="h-[60px]"></div>
              {decimalRange.map(d => (
                <div 
                  key={d}
                  className={`h-10 flex items-center justify-center snap-center font-display ${
                    editDecimal === d ? 'text-[#1d3557] font-bold text-2xl' : isDark ? 'text-white/30' : 'text-gray-300'
                  }`}
                >
                  {d}
                </div>
              ))}
              <div className="h-[60px]"></div>
            </div>
            <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-10 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">{t('common.cancel')}</button>
          <button onClick={onSave} disabled={saving} className="btn-primary flex-1">
            {saving ? t('weightHistory.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ log, isDark, onDelete, onClose, t, formatDateShort }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-hidden"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-7 h-7 text-red-500" />
        </div>
        <h3 className={`font-display text-lg mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('weightHistory.deleteTitle')}
        </h3>
        <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {t('weightHistory.deleteConfirm', { weight: Number(log.weight).toFixed(1), date: formatDateShort(log.date) })}
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">{t('common.cancel')}</button>
          <button onClick={onDelete} className="flex-1 py-3 px-4 rounded-full bg-red-500 text-white font-semibold">
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
